// API wrappers around the ClinPay panellist Edge Functions hosted on
// the project's Supabase. All routes are intentionally service-role-backed
// behind the function — the panellist holds only a per-survey JWT.

const SUPABASE_URL =
  (import.meta as unknown as { env?: { VITE_SUPABASE_URL?: string } }).env
    ?.VITE_SUPABASE_URL ?? 'https://wibwhwxsoutngqyjvhgz.supabase.co'

const FN_BASE = `${SUPABASE_URL}/functions/v1`

export interface SurveyMeta {
  surveyId: string
  name: string
  sponsor: { name: string }
  tiers: Array<{ name: string; amount: number; currency: string }>
  methods: Array<'sepa' | 'usdc' | 'gbp_fps' | 'usd_ach' | 'lightning'>
  status: string
  dryRun: boolean
  allowedOrigins: string[]
  expectedSettlement: string
  vatNote: string
  startDate: string
  endDate: string
}

export interface PayoutResponse {
  ok: true
  receiptId: string
  publicReceiptUrl: string
  status: 'pending' | 'processing' | 'test' | 'settled'
  dryRun: boolean
  expectedSettlement: string
  amountCents: number
  currency: string
  tier: string
  network?: string
  // Lightning-specific extras (only on /clinpay-payout-lightning):
  sats?: number
  fx_source?: 'kraken' | 'coingecko'
  fx_rate?: number
  preimage?: string
  resolved_address?: string | null
  settled_at?: number
}

export interface LightningQuoteResponse {
  ok: true
  quote_id: string
  sats: number
  amount_eur_cents: number
  currency: 'EUR'
  tier: string
  fx_rate: number
  fx_source: 'kraken' | 'coingecko'
  fx_timestamp: number
  expires_at: string
  lock_seconds: number
  dry_run: boolean
}

export interface ReceiptResponse {
  receiptId: string
  surveyName: string
  tier: string
  amount: number
  currency: string
  method: 'sepa' | 'usdc' | 'gbp_fps' | 'usd_ach' | 'lightning'
  status: 'pending' | 'processing' | 'settled' | 'failed' | 'refunded' | 'test'
  submittedAt: string
  settledAt: string | null
  statute: string
  statutePlain: string
  hash: {
    mandate: string
    anchor: string
    anchorTx: string
    anchorChain: string
  } | null
  verifiable: boolean
  lightning: {
    sats: number | null
    fxRate: number | null
    fxSource: 'kraken' | 'coingecko' | null
    fxTimestamp: string | null
    preimage: string | null
    paymentHash: string | null
  } | null
}

async function callJson<T>(
  url: string,
  init: RequestInit = {},
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string; raw?: unknown }> {
  let res: Response
  try {
    res = await fetch(url, init)
  } catch (e) {
    return { ok: false, status: 0, error: `network_error: ${(e as Error).message}` }
  }
  let raw: unknown = null
  try {
    raw = await res.json()
  } catch {
    return { ok: false, status: res.status, error: 'invalid_response' }
  }
  if (!res.ok) {
    const errMessage =
      (raw as { error?: string; message?: string })?.error ??
      (raw as { message?: string })?.message ??
      `http_${res.status}`
    return { ok: false, status: res.status, error: errMessage, raw }
  }
  return { ok: true, data: raw as T }
}

export function fetchSurvey(surveyId: string) {
  const url = `${FN_BASE}/clinpay-survey-public?surveyId=${encodeURIComponent(surveyId)}`
  return callJson<SurveyMeta>(url, { method: 'GET' })
}

export function submitSepaPayout(input: {
  surveyId: string
  panellistToken: string
  tier: string
  iban: string
  accountHolder: string
  // Postal address is required by the EU settlement rail (Depa) for
  // SEPA credit transfers to natural persons.
  addressLine1: string
  city: string
  postalCode: string
  country: string
  consent: boolean
}) {
  return callJson<PayoutResponse>(`${FN_BASE}/clinpay-payout-sepa`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

export function submitUsdcPayout(input: {
  surveyId: string
  panellistToken: string
  tier: string
  wallet: string
  network: string
  consent: boolean
}) {
  return callJson<PayoutResponse>(`${FN_BASE}/clinpay-payout-usdc`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

export function requestLightningQuote(input: {
  surveyId: string
  panellistToken: string
  tier: string
}) {
  return callJson<LightningQuoteResponse>(`${FN_BASE}/clinpay-quote-lightning`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

export function submitLightningPayout(input: {
  surveyId: string
  panellistToken: string
  tier: string
  quoteId: string
  addressOrInvoice: string
  consent: boolean
}) {
  return callJson<PayoutResponse>(`${FN_BASE}/clinpay-payout-lightning`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

export function fetchReceipt(receiptId: string) {
  const url = `${FN_BASE}/clinpay-receipt-public?receiptId=${encodeURIComponent(receiptId)}`
  return callJson<ReceiptResponse>(url, { method: 'GET' })
}
